import type { SearchResultHit, FolderSearchResultHit, Item } from '@mimir/common/dto/searchResultHit';
import mitt from 'mitt';
import type { ShowDialogCommand } from './dialogs/showDialogCommand';
import type { StartAnalyticJobParams } from './model/item-interfaces';
import type { AsyncOperationType } from '@mimir/common/dto/asyncOperationType';
import type { TabName } from '@mimir/common/types/UIOnlySections';
import type { FolderDto, FolderViewModel } from '@mimir/common/dto/folderModels';
import type { TimelineContextMenuPayload } from './components/Common/Timeline/timeline-utils';

type Events = {
  showToast: { message: string, color: TOAST, timeout: number };
  SendNcsItem: Item;
  HidePleaseWaitDialog: void;
  ShowDialog: ShowDialogCommand;
  SearchPage_Results_Loaded: boolean;
  SearchPage_Reset: void;
  doSearch: void;
  resetDateFilter: boolean | undefined;
  startAnalytics: StartAnalyticJobParams;
  stopAnalyticsJob: AsyncOperationType;
  playerJumpToTime: number;
  showTabInItemDetails: TabName;
  showTimedMdfMenu: void;
  setPlayerCurrentTimeToFocusedWord: void;
  downloadAudioChannels: void;
  refreshCurrentSearch: boolean | undefined;
  showFolderContextMenu: { ev: MouseEvent, folder: FolderViewModel | FolderDto | FolderSearchResultHit, minimizeFolderMenu?: boolean };
  showMultipleItemsContextMenu: { ev: MouseEvent, items: SearchResultHit[], browseMode: boolean };
  configReset: void;
  navigateBackFromItemDetails: void;
  triggerRelationsRefreshIfFocused: void;
  triggerSharesRefresh: void;
  resetDragZone: void;
  showTimelineTrackContextMenu: TimelineContextMenuPayload;
  saveMetadata: undefined;
  clientHookSaveMetadata: void;
  closeStorageMenuInsideContextMenu: void;
  saveColumnSizes: string;
  scrollToTimedMdf: { refId: string, event?: MouseEvent };
  toggleTheme: void;
  refineSelection: SearchResultHit[];
};
const eventBus = mitt<Events>();
export default eventBus;

export enum EVENTS {
  SEND_NCS_ITEM = 'SendNcsItem',
  HIDE_PLEASE_WAIT_DIALOG = 'HidePleaseWaitDialog',
  SHOW_DIALOG = 'ShowDialog',
  SEARCH_PAGE_RESULTS_LOADED = 'SearchPage_Results_Loaded',
  SEARCH_PAGE_RESET = 'SearchPage_Reset',
  SHOW_TOAST = 'showToast',
  DO_SEARCH = 'doSearch',
  RESET_DATEFILTER = 'resetDateFilter',
  START_ANALYTICS = 'startAnalytics',
  CANCEL_ANALYTICS_JOB = 'stopAnalyticsJob',
  PLAYER_JUMP_TO_TIME = 'playerJumpToTime',
  SHOW_TAB_IN_ITEM_DETAILS = 'showTabInItemDetails',
  SHOW_TIMED_MDF_MENU = 'showTimedMdfMenu',
  SET_PLAYER_CURRENTTIME_TO_FOCUSED_WORD = 'setPlayerCurrentTimeToFocusedWord',
  DOWNLOAD_AUDIO_CHANNELS = 'downloadAudioChannels',
  REFRESH_CURRENT_SEARCH = 'refreshCurrentSearch',
  SHOW_FOLDER_CONTEXT_MENU = 'showFolderContextMenu',
  SHOW_MULTIPLE_ITEMS_CONTEXT_MENU = 'showMultipleItemsContextMenu',
  CONFIG_RESET = 'configReset',
  NAVIGATE_BACK_FROM_ITEM = 'navigateBackFromItemDetails',
  TRIGGER_RELATIONS_REFRESH_IF_FOCUSED = 'triggerRelationsRefreshIfFocused',
  TRIGGER_SHARES_REFRESH = 'triggerSharesRefresh',
  RESET_DRAG_ZONE = 'resetDragZone',
  SHOW_TIMELINE_TRACK_CONTEXTMENU = 'showTimelineTrackContextMenu',
  SAVE_METADATA = 'saveMetadata',
  CLIENT_HOOK_SAVE_METADATA = 'clientHookSaveMetadata',
  CLOSE_STORAGE_MENU_INSIDE_CONTEXTMENU = 'closeStorageMenuInsideContextMenu',
  SAVE_COLUMN_SIZES = 'saveColumnSizes',
  SCROLL_TO_TIMED_MDF = 'scrollToTimedMdf',
  TOGGLE_THEME = 'toggleTheme',
  REFINE_SELECTION = 'refineSelection',
}

export enum TOAST {
  DEFAULT = '#06677d',
  SUCCESS = 'success',
  INFO = 'info',
  ERROR = 'error',
  WARNING = 'warning',
}

export const DEFAULT_TOAST_TIMEOUT = 5000; //ms
export const DONT_AUTO_HIDE_TOAST = -1;

export function showToast(message: string, color: TOAST, timeout = DEFAULT_TOAST_TIMEOUT) {
  eventBus.emit(EVENTS.SHOW_TOAST, { message, color, timeout });
}

export function showTabInItemDetailsPage(tabName: TabName) {
  eventBus.emit(EVENTS.SHOW_TAB_IN_ITEM_DETAILS, tabName);
}
